/** The view transition type the page cross-fade is styled under (`globals.css`). */
export const PAGE_TRANSITION_TYPE = 'page';

/** What the router tells the decision about a navigation (the part read here). */
interface LocationChange {
  pathChanged: boolean;
}

/** The router's `defaultViewTransition` object form, as far as it is used here. */
export interface PageTransitionOptions {
  types: (change: LocationChange) => string[] | false;
}

/**
 * Which navigations cross-fade: a change of page, never a change of state on
 * the same page.
 *
 * Opening a task's drawer, switching its tab or narrowing a board only change
 * the query, and a fade there would blur the very thing being looked at. The
 * morphs that open one thing into its page (spec 075 FR-026) ask for their
 * own transition on their links and are untouched by this. Off entirely when
 * the person asked their system for less motion (FR-029's spirit).
 */
export function pageTransitionTypes(
  change: LocationChange,
  reducedMotion: boolean,
): string[] | false {
  if (reducedMotion || !change.pathChanged) return false;
  return [PAGE_TRANSITION_TYPE];
}

/**
 * The router's `defaultViewTransition`: a short cross-fade between pages.
 *
 * Only where the browser supports view transition *types*. Without them the
 * router ignores the decision above and would animate every navigation,
 * query changes included; there, pages keep swapping instantly as before.
 */
export function pageViewTransition(): PageTransitionOptions | false {
  const supportsTypes =
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports(
      `selector(:active-view-transition-type(${PAGE_TRANSITION_TYPE}))`,
    );
  if (!supportsTypes) return false;
  return {
    types: (change) =>
      pageTransitionTypes(
        change,
        window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      ),
  };
}

/** The part of a view transition this module touches. */
interface SkippableTransition {
  ready: Promise<unknown>;
  finished: Promise<unknown>;
}

interface TransitionDocument {
  startViewTransition?: (...args: never[]) => SkippableTransition;
}

/**
 * Let a skipped page transition end quietly.
 *
 * A transition is skipped whenever another navigation starts before it has
 * run: a redirect right after sign-in, a second click mid-fade. The browser
 * then rejects its `ready` and `finished` with an `AbortError`. The router
 * awaits neither, so every skip surfaced as an unhandled rejection: noise in
 * the console and in error reporting for something that is not an error.
 * Anything other than that `AbortError` is left to surface as before.
 */
export function quietSkippedTransitions(doc: TransitionDocument = document) {
  const start = doc.startViewTransition;
  if (typeof start !== 'function') return;
  const ignoreSkip = (error: unknown) => {
    // A DOMException in browsers; read by name rather than by class.
    if ((error as { name?: unknown } | null)?.name === 'AbortError') return;
    throw error;
  };
  doc.startViewTransition = (...args: never[]) => {
    const transition = start.apply(doc, args);
    transition.ready.catch(ignoreSkip);
    transition.finished.catch(ignoreSkip);
    return transition;
  };
}
